/**
 * Same-browser resume for hub + card onboarding.
 *
 * Return always lands on the hub once a package is chosen. Position inside a
 * drill-in is not persisted — interruption is cheap relative to a 7-phase
 * flow if the hub shows what's done.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { perTabStorage } from '@/stores/perTabStorage';
import type { ProductPackage } from '@/types/auth';
import type { HubJob } from './jobs';

/** Draft lives here until a Business (workspace) exists. */
export const PRE_TENANT_KEY = '__new__';

export interface OnboardingDraft {
  package: ProductPackage | null;
  completedJobs: HubJob[];
  businessName: string;
  domain: string;
  locations: string[];
  pluginKey: string;
  pluginSkipped: boolean;
  justMe: boolean;
  inviteEmails: string[];
  /** Proved site URL from the authenticated retrieval. */
  siteUrl: string | null;
  foundationDone: string[];
}

export const emptyDraft = (): OnboardingDraft => ({
  package: null,
  completedJobs: [],
  businessName: '',
  domain: '',
  locations: [],
  pluginKey: '',
  pluginSkipped: false,
  justMe: false,
  inviteEmails: [],
  siteUrl: null,
  foundationDone: [],
});

interface OnboardingState {
  draftByKey: Record<string, OnboardingDraft>;
  draftFor: (key: string) => OnboardingDraft;
  patchDraft: (key: string, next: Partial<OnboardingDraft>) => void;
  completeJob: (key: string, job: HubJob) => void;
  /** Copy the pre-tenant draft onto the new tenant key after workspace create. */
  adoptTenant: (tenantId: string) => void;
  clearKey: (key: string) => void;
}

const omit = <T,>(record: Record<string, T>, key: string): Record<string, T> => {
  const next = { ...record };
  delete next[key];
  return next;
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      draftByKey: {},

      draftFor: (key) => get().draftByKey[key] ?? emptyDraft(),

      patchDraft: (key, next) =>
        set((s) => ({
          draftByKey: {
            ...s.draftByKey,
            [key]: { ...emptyDraft(), ...s.draftByKey[key], ...next },
          },
        })),

      completeJob: (key, job) =>
        set((s) => {
          const current = { ...emptyDraft(), ...s.draftByKey[key] };
          if (current.completedJobs.includes(job)) return s;
          return {
            draftByKey: {
              ...s.draftByKey,
              [key]: {
                ...current,
                completedJobs: [...current.completedJobs, job],
              },
            },
          };
        }),

      adoptTenant: (tenantId) =>
        set((s) => {
          const pre = s.draftByKey[PRE_TENANT_KEY];
          if (!pre) return s;
          return {
            draftByKey: {
              ...omit(s.draftByKey, PRE_TENANT_KEY),
              [tenantId]: pre,
            },
          };
        }),

      clearKey: (key) => set((s) => ({ draftByKey: omit(s.draftByKey, key) })),
    }),
    {
      name: 'onboarding-hub-v1',
      storage: createJSONStorage(perTabStorage),
      // The plugin key is a credential. Keep it only in live memory; if the
      // user reloads, the server-side key-presence flag lets them prove with
      // the already saved key without putting the secret in browser storage.
      partialize: (s) => ({
        draftByKey: Object.fromEntries(
          Object.entries(s.draftByKey).map(([key, draft]) => [
            key,
            { ...draft, pluginKey: '' },
          ]),
        ),
      }),
    },
  ),
);
